import React, { useState, useEffect } from "react";
import { createPhrase, updatePhrase } from "./adminPhraseService";
import "../../styles/content.css";

const TYPES = ["PHRASE", "ABBREVIATION", "IDIOM", "PHRASAL_VERB"];
const CATEGORIES = ["EVERYDAY", "OFFICE", "ACADEMIC", "BUSINESS", "MEDICAL", "LEGAL", "TECH", "FINANCE", "TRAVEL", "SOCIAL"];
const DIFFICULTIES = ["BEGINNER", "INTERMEDIATE", "ADVANCED"];

const defaultForm = {
  title: "", fullForm: "", meaning: "", usageNote: "", exampleSentence: "",
  pronunciation: "", phoneticSpelling: "", youtubeLink: "", type: "PHRASE", category: "EVERYDAY",
  difficulty: "BEGINNER", sortOrder: "",
};

const PhraseForm = ({ phrase, onSaved, onCancel }) => {
  const isEdit = !!phrase;
  const [form, setForm] = useState(defaultForm);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (phrase) {
      console.log("[PhraseForm] Populating form for edit, id:", phrase.id);
      setForm({
        title: phrase.title || "",
        fullForm: phrase.fullForm || "",
        meaning: phrase.meaning || "",
        usageNote: phrase.usageNote || "",
        exampleSentence: phrase.exampleSentence || "",
        pronunciation: phrase.pronunciation || "",
        phoneticSpelling: phrase.phoneticSpelling || "",
        youtubeLink: phrase.youtubeLink || "",
        type: phrase.type || "PHRASE",
        category: phrase.category || "EVERYDAY",
        difficulty: phrase.difficulty || "BEGINNER",
        sortOrder: phrase.sortOrder ?? "",
      });
    }
  }, [phrase]);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const formatLabel = (s) => s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

  const handleSubmit = async () => {
    if (!form.title || !form.meaning) {
      console.warn("[PhraseForm] Validation failed: missing title or meaning");
      alert("Title and Meaning are required");
      return;
    }
    const payload = {
      ...form,
      fullForm: form.fullForm || null,
      youtubeLink: form.youtubeLink || null,
      sortOrder: form.sortOrder !== "" ? Number(form.sortOrder) : null,
    };
    console.log("[PhraseForm] Submitting", isEdit ? "update" : "create", "payload:", payload);
    setLoading(true);
    const res = isEdit ? await updatePhrase(phrase.id, payload) : await createPhrase(payload);
    if (res.status === 200 || res.status === 201) {
      console.log("[PhraseForm] Submit successful:", res.status);
      onSaved();
    } else {
      console.error("[PhraseForm] Submit failed:", res.status, res.message);
      alert(res.message || "Operation failed");
    }
    setLoading(false);
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content" style={{ width: 600, maxHeight: "90vh", overflowY: "auto" }}>
        <div className="form-card" style={{ margin: 0, boxShadow: "none" }}>
          <h2>{isEdit ? "Edit" : "Create"} Phrase</h2>

          <div className="form-group">
            <label>Title *</label>
            <input name="title" placeholder="e.g. PTO, break the ice" value={form.title} onChange={handleChange} />
          </div>

          <div className="form-group">
            <label>Full Form (for abbreviations)</label>
            <input name="fullForm" placeholder="e.g. Personal Time Off" value={form.fullForm} onChange={handleChange} />
          </div>

          <div className="form-group">
            <label>Meaning *</label>
            <textarea name="meaning" placeholder="What does it mean?" value={form.meaning} onChange={handleChange} rows={2} />
          </div>

          <div className="form-group">
            <label>Usage</label>
            <textarea name="usageNote" placeholder="When/how is it used?" value={form.usageNote} onChange={handleChange} rows={2} />
          </div>

          <div className="form-group">
            <label>Example Sentence</label>
            <input name="exampleSentence" placeholder="e.g. I'll call you as soon as I reach." value={form.exampleSentence} onChange={handleChange} />
          </div>

          <div className="form-group">
            <label>Pronunciation (IPA)</label>
            <input name="pronunciation" placeholder="/æz suːn æz/" value={form.pronunciation} onChange={handleChange} />
          </div>

          <div className="form-group">
            <label>Phonetic Spelling</label>
            <input name="phoneticSpelling" placeholder="az SOON az" value={form.phoneticSpelling} onChange={handleChange} />
          </div>

          <div className="form-group">
            <label>YouTube Link (optional)</label>
            <input name="youtubeLink" placeholder="https://youtube.com/watch?v=..." value={form.youtubeLink} onChange={handleChange} />
          </div>

          <div style={{ display: "flex", gap: 12 }}>
            <div className="form-group" style={{ flex: 1 }}>
              <label>Type</label>
              <select name="type" value={form.type} onChange={handleChange}>
                {TYPES.map((t) => <option key={t} value={t}>{formatLabel(t)}</option>)}
              </select>
            </div>
            <div className="form-group" style={{ flex: 1 }}>
              <label>Category</label>
              <select name="category" value={form.category} onChange={handleChange}>
                {CATEGORIES.map((c) => <option key={c} value={c}>{formatLabel(c)}</option>)}
              </select>
            </div>
          </div>

          <div style={{ display: "flex", gap: 12 }}>
            <div className="form-group" style={{ flex: 1 }}>
              <label>Difficulty</label>
              <select name="difficulty" value={form.difficulty} onChange={handleChange}>
                {DIFFICULTIES.map((d) => <option key={d} value={d}>{formatLabel(d)}</option>)}
              </select>
            </div>
            <div className="form-group" style={{ flex: 1 }}>
              <label>Sort Order</label>
              <input name="sortOrder" type="number" placeholder="e.g. 1" value={form.sortOrder} onChange={handleChange} />
            </div>
          </div>

          <div className="form-actions">
            <button onClick={handleSubmit} disabled={loading}>
              {loading ? "Saving..." : isEdit ? "Update" : "Create"}
            </button>
            <button onClick={onCancel} disabled={loading}>Cancel</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PhraseForm;
